import { useState } from 'react'

import { Trans } from '@lingui/react'
import { isNil } from 'ramda'

import { useResendOtpVerificationCodeMutation } from '@twisto/api-graphql'
import { Flex } from '@twisto/components/atoms/flex'
import { useToast } from '@twisto/components/atoms/toast'
import { ResendButton } from '@twisto/components/molecules/resend-button'
import { useFlow } from '@twisto/flow-views/flow/flow.context'
import { getFieldResponseError, getFirstResponseError } from '@twisto/form'

import * as styles from './otp-verification-resend-button.styles'

type Props = {
  variant: 'sms' | 'email'
  resendPossibleAt: string | null
}
export const OtpVerificationResendButton = ({
  resendPossibleAt,
  variant,
}: Props) => {
  const { sessionId } = useFlow()
  const [codeSent, setCodeSent] = useState(false)
  const { showToast } = useToast()

  const [resendCode, { loading: submittingResend }] =
    useResendOtpVerificationCodeMutation({
      variables: {
        auth: {
          sessionId,
        },
      },
      context: { overrideNonFieldErrorHandler: true },
      onCompleted: (response) => {
        const fieldError = getFirstResponseError(
          response.resendOtpVerificationCode
        )
        const nonFieldError = getFieldResponseError(
          response.resendOtpVerificationCode,
          ['nonFieldErrors']
        )
        if (!isNil(fieldError) || !isNil(nonFieldError)) {
          return showToast({
            message: fieldError ?? nonFieldError ?? '',
            variant: 'error',
          })
        }

        const currentStep =
          response.resendOtpVerificationCode.success?.flow.currentStep
        const codeSent =
          !isNil(currentStep) && 'codeSent' in currentStep
            ? currentStep.codeSent
            : false

        setCodeSent(codeSent)
      },
    })

  if (isNil(resendPossibleAt)) {
    return null
  }

  return (
    <Flex alignItems="center" css={styles.resendButton} justify="center">
      <ResendButton
        actionAvailableAt={resendPossibleAt}
        buttonProps={{
          color: 'secondary',
          disabled: submittingResend,
        }}
        setSuccess={setCodeSent}
        success={codeSent}
        variant={variant}
        onClick={resendCode}
      >
        {variant === 'email' ? (
          <Trans id="flow.otpVerificationEmailStep.resendButton" />
        ) : (
          <Trans id="flow.otpVerificationSmsStep.resendButton" />
        )}
      </ResendButton>
    </Flex>
  )
}
